import React, { useState } from 'react';
import { Button, ListGroup, Modal } from 'react-bootstrap';
import type { FuLocale } from '../../i18n/fuLocale';
import './CommandHistoryButton.css';

export type StateTimelineItem = {
  commandString: string;
  isSensitive: boolean;
  redactedCommandString: string;
  timestamp: number;
  initiatorName: string;
};

interface CommandHistoryButtonProps {
  stateTimeline: StateTimelineItem[];
  position: number;
  onMove: (position: number) => void;
  onRollback: (position: number) => void;
  isHost: boolean;
  locale?: FuLocale;
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString();
}

function getDisplayText(entry: StateTimelineItem, isHost: boolean): string {
  return entry.isSensitive && !isHost ? entry.redactedCommandString : entry.commandString;
}

export const CommandHistoryButton: React.FC<CommandHistoryButtonProps> = ({
  stateTimeline,
  position,
  onMove,
  onRollback,
  isHost,
  locale = 'zh',
}) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        size="lg"
        className="command-history-button"
        onClick={() => setShowModal(true)}
        title="查看状态时间线"
      >
        状
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>状态时间线</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button variant="outline-secondary" disabled={!isHost || position === 0} onClick={() => onMove(position - 1)}>
              ↶ 回退一步
            </Button>
            <span className="text-muted">可回退 {position} 步 · 可前进 {stateTimeline.length - position} 步</span>
            <Button variant="outline-secondary" disabled={!isHost || position === stateTimeline.length} onClick={() => onMove(position + 1)}>
              前进一步 ↷
            </Button>
          </div>
          {isHost && position < stateTimeline.length && (
            <div className="d-flex justify-content-center mb-3">
              <Button variant="danger" onClick={() => { if (window.confirm("确认回溯到当前预览节点？之后的状态变更将被删除。")) onRollback(position); }}>确认回溯</Button>
            </div>
          )}
          <ListGroup>
            <ListGroup.Item
              action={isHost}
              className={`state-timeline-item ${position === 0 ? 'state-timeline-item--applied' : 'state-timeline-item--future'}`}
              onClick={isHost ? () => onMove(0) : undefined}
            >
              <strong>{locale === 'zh' ? '第 0 条 · 初始/上传状态' : 'Entry 0 · Initial/uploaded state'}</strong>
              <span className={`float-end badge state-timeline-status ${position === 0 ? 'state-timeline-status--applied' : 'state-timeline-status--future'}`}>
                {position === 0 ? '已执行' : '可前进'}
              </span>
            </ListGroup.Item>
              {stateTimeline.map((entry, index) => {
                const isApplied = index < position;
                return (
                <ListGroup.Item
                  key={`${entry.timestamp}-${index}`}
                  action={isHost}
                  className={`state-timeline-item ${isApplied ? 'state-timeline-item--applied' : 'state-timeline-item--future'}`}
                  onClick={isHost ? () => onMove(index + 1) : undefined}
                >
                  <code className="state-timeline-command">{getDisplayText(entry, isHost)}</code>
                  <small className="text-muted ms-2">{formatTime(entry.timestamp)}</small>
                  {entry.initiatorName && <small className="text-muted ms-2">{entry.initiatorName}</small>}
                  <span className={`float-end badge state-timeline-status ${isApplied ? 'state-timeline-status--applied' : 'state-timeline-status--future'}`}>
                    {isApplied ? '已执行' : '可前进'}
                  </span>
                </ListGroup.Item>
                );
              })}
          </ListGroup>
        </Modal.Body>
      </Modal>
    </>
  );
};
